import React, {useState, useEffect} from 'react';
import PageContent from "../../../components/page-content/page-content";
import {Button, Card, Col, Row, Space, Table, Tag, Select, message} from "antd";
import {PlusOutlined} from "@ant-design/icons";
import {getStocks, copyStocksFromPreviousYear} from "../../../lib/api_stock";
import {getcwcoaAll} from "../../../lib/api_cw_coa";
import Stock_add_modal from "./stock_add_modal";
import Stock_edit_modal from "./stock_edit_modal";
import Stock_package_modal from "./stock_package_modal";
import DictName from "../../../components/dict/dict-name";
import moment from "moment";

const {Column} = Table;
const Stock = () => {
    const [list, setlist] = useState([]);
    const [showStockAddModal, setShowStockAddModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [packageId, setPackageId] = useState(0);
    const [selectedYear, setSelectedYear] = useState(moment().year());
    const [selectedType, setSelectedType] = useState(null);
    const [tableParams, setTableParams] = useState({
        pagination: {
            current: 1,
            pageSize: 20,
        },
    });
    const [hasData, setHasData] = useState(false);
    const [copyLoading, setCopyLoading] = useState(false);
    const [coaData, setCoaData] = useState([]);

    // 生成年份选项，从2024年开始到当前年份
    const yearOptions = [];
    const currentYear = moment().year();
    for (let year = 2024; year <= currentYear + 1; year++) {
        yearOptions.push(year);
    }

    const fetchData = () => {
        getStocks(tableParams.pagination.current, tableParams.pagination.pageSize, selectedYear, selectedType)
            .then((res) => {
                if (res.code === 0) {
                    const dataList = res.data.list || [];
                    setlist(dataList);
                    setHasData(dataList.length > 0);
                    setTableParams({
                        ...tableParams,
                        pagination: {
                            ...tableParams.pagination,
                            total: res.data.total || 0,
                        },
                    });
                }
            })
    }

    const fetchCoaData = () => {
        getcwcoaAll(selectedYear)
            .then((res) => {
                if (res.code === 0) {
                    setCoaData(res.data || []);
                }
            })
    }

    useEffect(fetchData, [JSON.stringify(tableParams), selectedYear, selectedType]);
    useEffect(fetchCoaData, [selectedYear]);

    const handleTableChange = (pagination, filters, sorter) => {
        setTableParams({
            pagination,
            filters,
            ...sorter,
        });
    };

    const handleCopyFromPreviousYear = () => {
        setCopyLoading(true);
        copyStocksFromPreviousYear(selectedYear)
            .then((res) => {
                if (res.code === 0) {
                    message.success('复制成功');
                    fetchData();
                } else {
                    message.error(res.message || '复制失败');
                }
            })
            .finally(() => {
                setCopyLoading(false);
            });
    };

    const getCoaName = (coaId) => {
        if (!coaId) return '-';
        const coa = coaData.find(item => item.id === coaId);
        return coa ? coa.name : `未知科目(${coaId})`;
    };

    return (
        <PageContent>
            <Card title={"库存管理"}>
                <Row justify="space-between">
                    <Col>
                        <Space>
                            <span>年份：</span>
                            <Select
                                value={selectedYear}
                                onChange={setSelectedYear}
                                style={{ width: 120 }}
                            >
                                {yearOptions.map(year => (
                                    <Select.Option key={year} value={year}>{year}年</Select.Option>
                                ))}
                            </Select>
                            
                            <span>类型：</span>
                            <Select
                                value={selectedType}
                                onChange={setSelectedType}
                                style={{ width: 120 }}
                                placeholder="全部类型"
                                allowClear
                            >
                                <Select.Option value={1}>普通商品</Select.Option>
                                <Select.Option value={2}>组合套餐</Select.Option>
                            </Select>
                        </Space>
                    </Col>
                    <Col>
                        <Space>
                            {!hasData && selectedYear > 2024 && (
                                <Button 
                                    type="primary" 
                                    loading={copyLoading}
                                    onClick={handleCopyFromPreviousYear}
                                >
                                    从上一年复制
                                </Button>
                            )}
                            <Button type="primary" icon={<PlusOutlined/>}
                                    onClick={() => setShowStockAddModal(true)}>添加库存</Button>
                            <Button type="primary" icon={<PlusOutlined/>}
                                    onClick={() => setPackageId(-1)}>添加组合商品</Button>
                        </Space>
                    </Col>
                </Row>
                <Table
                    dataSource={list}
                    rowKey={"id"}
                    pagination={{
                        ...tableParams.pagination,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total) => `共 ${total} 条`
                    }}
                    onChange={handleTableChange}
                    size={"small"}
                >
                    <Column
                        title="类型"
                        dataIndex="type"
                        key="类型"
                        width={100}
                        render={(type) => {
                            if (type === 1) {
                                return <Tag color="blue">普通商品</Tag>;
                            } else if (type === 2) {
                                return <Tag color="green">组合套餐</Tag>;
                            }
                            return type;
                        }}
                    />
                    <Column
                        title="所属科目"
                        dataIndex="coa_id"
                        key="所属科目"
                        width={120}
                        render={(coa_id) => getCoaName(coa_id)}
                    />
                    <Column
                        title="名称"
                        dataIndex="name"
                        key="名称"
                    />
                    <Column
                        title="分类"
                        dataIndex="category"
                        key="分类"
                        render={(category) => <DictName value={category} type_value="stocks_category"/>}
                    />
                    <Column
                        title="规格"
                        dataIndex="specification"
                        key="规格"
                    />
                    <Column
                        title="数量"
                        dataIndex="quantity"
                        key="数量"
                    />
                    <Column
                        title="单位"
                        dataIndex="unit"
                        key="单位"
                        render={(unit) => <DictName value={unit} type_value="stocks_unit"/>}
                    />
                    <Column
                        title="单价"
                        dataIndex="price"
                        key="单价"
                        render={(price) => price ? `¥${price}` : ''}
                    />
                    <Column
                        title="供应商"
                        dataIndex="supplier"
                        key="供应商"
                    />
                    <Column
                        title="操作"
                        key="操作"
                        render={
                            (text, record) => (
                                <Space>
                                    {record.type === 1 ? (
                                        <a onClick={() => setEditId({id: record.id, year: record.year})}>编辑</a>
                                    ) : (
                                        <a onClick={() => setPackageId(record.id)}>编辑</a>
                                    )}
                                </Space>
                            )
                        }
                    />
                </Table>
            </Card>
            {showStockAddModal &&
                <Stock_add_modal
                    year={selectedYear}
                    onOk={() => {
                        setShowStockAddModal(false);
                        fetchData();
                    }}
                    onCancel={() => {
                        setShowStockAddModal(false);
                    }}
                />
            }

            {editId && editId.id > 0 &&
                <Stock_edit_modal
                    id={editId.id}
                    year={editId.year}
                    onOk={() => {
                        setEditId(null);
                        fetchData();
                    }}
                    onCancel={() => {
                        setEditId(null);
                    }}
                />
            }

            {packageId !== 0 &&
                <Stock_package_modal
                    packageId={packageId}
                    year={selectedYear}
                    onOk={() => {
                        setPackageId(0);
                        fetchData();
                    }}
                    onCancel={() => {
                        setPackageId(0);
                        fetchData();
                    }}
                />
            }
        </PageContent>
    );
}

export default Stock;
